// English (US) language pack
export default {
  __meta: { code: 'en-US', label: 'English', short: 'EN' },

  common: {
    yes: 'Yes',
    no: 'No',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    submit: 'Submit',
    loading: 'Loading…',
    retry: 'Retry',
    back: 'Back',
    next: 'Next',
    prev: 'Previous',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    all: 'All',
    more: 'More',
    less: 'Less',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    copy: 'Copy',
    share: 'Share',
    minutesAgo: '{n} min ago',
    hoursAgo: '{n}h ago',
    daysAgo: '{n}d ago',
    online: 'Online',
    offline: 'Offline'
  },

  nav: {
    home: 'Home',
    problems: 'Problems',
    teams: 'Teams',
    leaderboard: 'Leaderboard',
    chain: 'Chain',
    academy: 'Academy',
    videos: 'HP Hackathon',
    login: 'Log in',
    register: 'Sign up',
    logout: 'Log out',
    profile: 'Profile',
    language: 'Language'
  },

  home: {
    title: 'Solve the {n} hardest problems in the world with AI',
    subtitle: 'From Riemann to room-temperature superconductors, from consciousness to fusion',
    desc: 'Anyone can use AI to attempt these. Every solution earns {reward}.',
    reward: 'on-chain HPW points',
    cta: 'Start solving →',
    ctaLeaderboard: 'Leaderboard',
    statsProblems: 'Problems',
    statsSolved: 'Solutions',
    statsUsers: 'Solvers',
    statsRewards: 'HPW paid',
    categories: '{n} disciplines',
    categoriesDesc: 'Pick a field to see what humanity has yet to solve',
    viewAll: 'All problems →',
    liveOnChain: '{txs} on-chain txs · {blocks} blocks'
  },

  problems: {
    title: 'All hard problems',
    subtitle: '{n} challenges across 8 disciplines',
    searchPlaceholder: 'Search problems, tags, keywords…',
    filterCategory: 'Category',
    filterStatus: 'Status',
    statusAll: 'All',
    statusOpen: 'Open',
    statusPartial: 'Partial',
    statusSolved: 'Solved',
    difficulty: 'Difficulty',
    reward: 'Reward',
    solutions: 'Solutions',
    votes: 'Net votes',
    tags: 'Tags',
    year: 'Year',
    proposer: 'Proposer',
    empty: 'No problems match your search'
  },

  problem: {
    back: '← Back to problems',
    info: 'Info',
    kidExplain: 'Kid-friendly explanation',
    formalStatement: 'Formal statement',
    whyHard: 'Why it\'s hard',
    howToEarn: 'How to earn points',
    earnSubmit: 'Submit any solution: +{n} HPW',
    earnAi: 'AI score ≥ {n}: +{m} HPW per point',
    earnVote: 'Upvotes (each): +{n} HPW to author',
    earnFullSolve: 'Full solve: up to {n} HPW',
    earnRule: 'All points auto-stake in 5s. Permanent on-chain.',
    aiSolver: 'AI assistant',
    aiSolverDesc: 'Tell AI your angle. It will answer in 3 layers: popular → academic → research direction.',
    aiUsingLLM: 'using real LLM',
    aiFallback: 'heuristic fallback',
    userInputPlaceholder: '(optional) e.g. explain with an example?',
    runAi: 'Ask AI',
    noSolution: 'No solution yet. Be the first!',
    submitSolution: 'Submit solution',
    submittedBy: 'by {user}',
    quality: 'AI score',
    selfTest: 'My solution',
    openChat: 'Open AI assistant',
    inputPlaceholder: 'Ask the AI a specific question…',
    continueChat: 'Continue chat ({n} messages)',
    openAI: '💬 Open AI assistant'
  },

  submit: {
    title: 'My solution',
    contentPlaceholder: 'Write down your thoughts. At least 20 chars. Better structure = higher score.',
    tooShort: 'Solution too short (min 20 chars)',
    submitted: 'Submitted!',
    submitFailed: 'Submit failed: {msg}'
  },

  leaderboard: {
    title: 'Leaderboard',
    subtitle: 'Sorted by on-chain HPW. New block every 5 seconds.',
    solvers: 'Solvers',
    solutions: 'Total solutions',
    txOnChain: 'On-chain txs',
    height: 'Chain height',
    rewardsPaid: 'Rewards paid',
    rank: 'Rank',
    score: 'Score',
    badges: 'Badges',
    badgesTitle: '{n}-tier challenger badges'
  },

  chain: {
    title: 'Blockchain explorer',
    subtitle: 'Every point transaction is immutably on-chain. Click a block to view details.',
    height: 'Height (blocks)',
    totalTxs: 'Total txs',
    totalSupply: 'Total supply (HPW)',
    valid: 'Valid',
    invalid: 'Invalid',
    selectBlock: 'Click a block on the left to view transactions',
    latestBlocks: 'Latest blocks',
    latestTxs: 'Latest txs',
    txId: 'Tx ID',
    type: 'Type',
    to: 'To',
    amount: 'Amount',
    time: 'Time',
    empty: 'No data'
  },

  auth: {
    title: 'Join the hard problems',
    subtitle: 'Create an account and start solving',
    username: 'Username',
    usernameHint: '2-30 chars, alphanumeric, _, CJK, -',
    password: 'Password',
    passwordHint: '6-200 chars',
    bio: 'Bio (optional)',
    bioHint: 'max 200 chars',
    register: 'Sign up',
    login: 'Log in',
    switchToLogin: 'Have an account? Log in',
    switchToRegister: 'No account? Sign up',
    welcome: '100 HPW welcome bonus',
    errUsername: 'Username must be 2-30 chars',
    errPassword: 'Password must be 6-200 chars',
    errTaken: 'Username taken',
    errWrong: 'Wrong username or password'
  },

  // Collaboration (v1.4.0)
  collab: {
    discussions: 'Discussions',
    roadmap: 'Roadmap',
    teams: 'Teams',
    teamsSubtitle: 'Form teams with fellow problem-solvers. Contributions aggregate, scores pool together.',
    teamsFor: 'Teams for this problem',
    teamsCount: 'team(s)',
    selectProblem: 'Select a problem',
    chooseProblem: 'Choose a problem',
    pickProblemFirst: 'Pick a problem above to see or create teams',
    startDiscussion: 'Start a new discussion…',
    post: 'Post',
    reply: 'Reply',
    replyPlaceholder: 'Write your reply…',
    upvote: 'Upvote',
    downvote: 'Downvote',
    edited: 'edited',
    noDiscussions: 'No discussions yet — start one!',
    loginRequired: 'Please log in first',
    loginToDiscuss: 'Log in to join the discussion',
    loginToContribute: 'Log in to add roadmap entries',
    confirmDelete: 'Are you sure you want to delete this? This cannot be undone.',
    roadmapSubtitle: 'Track research / breakthrough progress: proposed, exploring, in progress, breakthrough, blocked, done',
    roadmapTitlePlaceholder: 'Progress title (e.g. 2026 Q3: literature survey)',
    roadmapDescPlaceholder: 'Optional description',
    addEntry: 'Add entry',
    noRoadmap: 'No roadmap entries yet',
    createTeam: 'Create team',
    teamNamePlaceholder: 'Team name (unique per problem)',
    teamDescPlaceholder: 'Team description (optional)',
    create: 'Create',
    noTeamsYet: 'No teams yet',
    beFirstToCreate: 'Be the first to create one!',
    leader: 'Leader',
    member: 'Member',
    members: 'members',
    joinTeam: 'Join team',
    confirmLeave: 'Leave this team?',
    confirmDisband: 'Disband this team? This cannot be undone!',
    disband: 'Disband',
    leaveTeam: 'Leave',
    contributions: 'Member contributions',
    noContributions: 'No contributions yet',
    solutions: 'Solutions',
    score: 'Score',
    teamNotFound: 'Team not found or already disbanded',
    backToTeams: 'Back to teams',
    youAreLeader: 'You are the leader',
    youAreMember: 'You are a member'
  },

  notFound: {
    title: '404 — Page not found',
    desc: 'The page you\'re looking for doesn\'t exist, or hasn\'t been indexed yet. How about checking out {n} hard problems?',
    home: '← Home',
    browse: 'Browse problems',
    rank: 'Leaderboard'
  },

  pwa: {
    install: 'Install HardProblems to your device for quick access',
    installShort: 'Install',
    update: 'New version ready',
    updateNow: 'Update now',
    offline: 'You\'re offline · some features unavailable'
  },

  categories: {
    mathematics: 'Mathematics',
    physics: 'Physics',
    chemistry: 'Chemistry',
    biology: 'Biology',
    cs: 'Computer Science',
    philosophy: 'Philosophy',
    engineering: 'Engineering',
    social: 'Social Sciences'
  },

  // Hardcoded strings extracted from JSX
  hardcoded: {

  },

  // Hardcoded strings extracted from JSX
  hardcoded: {
    hc_87285292: '🎖 Challenger Medal',
    hc_38149815: 'Last Block',
    hc_48616739: 'No blocks yet',
    hc_57878265: 'Latest Transactions',
    hc_63698773: 'Transaction ID',
    hc_1714432: '&Genre',
    hc_76617209: 'To',
    hc_86455028: 'Fill quantity',
    hc_45733203: 'Time',
    hc_4062857: 'No transaction yet',
    hc_15614928: '<g id="Bold">Transaction Detailed:</g>',
    hc_12461780: 'None (Genesis block)',
    hc_36065070: 'Memuat...',
    hc_39726153: 'The player doesn\'t exist',
    hc_65423691: 'Next Medal:',
    hc_65829733: 'HPW Balance',
    hc_33844058: 'Number of answers',
    hc_34880504: 'On-chain transactions',
    hc_64546944: 'Get in total:',
    hc_63635099: '🔑 Blockchain wallet address',
    hc_92114295: 'Answer',
    hc_12374108: '📨 On-chain recording',
    hc_38642490: 'No on-chain transactions yet',
    hc_39272624: 'All Hard Questions',
    hc_72504969: 'On-chain',
    hc_76554194: 'monetary business deal',
    hc_13199282: 'problem solvers',
    hc_49455724: 'Resolve',
    hc_15553251: 'The hardest thing in the world',
    hc_73115764: 'questions.',
    hc_58891330: 'From the Riemann hypothesis to room temperature superconductivity, from the nature of consciousness to nuclear fusion.',
    hc_11135829: 'On-chain HPW Points Rewards',
    hc_14271420: 'Video number search',
    hc_93127099: '"HP Hackathon"',
    hc_95444243: '· Ignites curiosity for 1 minute every day · WeChat search and follow',
    hc_48803929: 'Search "HP Hackathon" on WeChat · 1 min a day to spark curiosity',
    hc_4038150: 'Question is missing.',
    hc_1415707: '<<< Back to questions',
    hc_3109735: 'How 🙋 can I participate?',
    hc_24717615: 'You don\'t need to be an expert. You can help with this in any of the following ways:',
    hc_6216823: 'Ask AI',
    hc_49022408: 'or at',
    hc_72042925: 'Ranking',
    hc_60372142: '💬 AI Assistant · Multiple Conversations',
    hc_98658150: 'Kids Edition',
    hc_11457022: 'Strict statement',
    hc_53488: 'Why is it hard?',
    hc_24903028: 'Thinking...',
    hc_67511018: 'AI-based content modified and then submitted will be marked as "AI-assisted"',
    hc_74081354: '✍️ Submit your answer',
    hc_8743312: '5-Dimension Assessment',
    hc_79230102: '',
    hc_9486386: ' earned',
    hc_10506083: 'Blockchain',
    hc_60763393: '🤝 Looking to team up for answers?',
    hc_817654: 'Question Team Page',
    hc_91839987: '📋 Question meta information',
    hc_7972472: 'Year of submission',
    hc_4585175: 'proposer',
    hc_49772229: 'Difficulty',
    hc_6351505: 'Status',
    hc_77609957: 'Basic reward',
    hc_59943341: '💰 How do I earn points?',
    hc_1469249: '· Submit any answers:',
    hc_66284549: '· AI score ≥ 60:',
    hc_29791639: '· Liked (per person per time):',
    hc_97186354: '+5 HPW to author',
    hc_18280432: '· Solve the puzzle:',
    hc_76767612: 'All points are automatically wound within 5 seconds and are permanently visible.',
    hc_55655280: '🌟 Share this hard question',
    hc_9145530: 'Benefits :',
    hc_9755158: 'Tidak Cukup',
    hc_78448776: '🤖 AI-assisted',
    hc_99426362: 'AI Assist',
    hc_40752456: 'AI has read all the context of this question. Let\'s start asking ↓',
    hc_67183189: 'AI is thinking...',
    hc_38124998: 'Send',
    hc_34225746: 'Something went wrong',
    hc_29662853: 'Chain height',
    hc_84566882: 'Block',
    hc_41149725: 'deals',
  }
};
